import { SymbologyValues } from 'app/store/appSettings/AppSettingsState';

import { buildSymbolDependentRequestHelpers } from 'widgets/streaming/buildSymbolDependentRequestHelpers';
import { FillsGraphByOrderIdOrSymQtyStreamRequestWithSymQty } from 'communication/types/fillsGraph/FillsGraphByOrderIdOrSymQtyStreamRequest';
import { buildFillsGraphRequest } from 'widgets/fillsGraph/store/data/FillsGraphStreamRequest';
import { PortfolioSelectionWrapper } from 'portfolio/PortfolioSelectionWrapper';
import { buildPortfolioSelection } from 'portfolio/PortfolioSelection';
import { defaultFillsGraphConfiguration } from 'widgets/fillsGraph/store/configuration/FillsGraphConfiguration';
import {
    StreamsByStreamKeyState,
    StreamState,
} from 'streamsState/store/streamedData/StreamedDataState';
import {
    FillsGraphDataState,
    initialFillsGraphDataState,
} from 'widgets/fillsGraph/store/data/FillsGraphDataState';
import State from 'store/State';
import { DataServices } from 'store/Services';
import { selectAppSettings } from 'app/store/appSettings/selectors';
import {
    IndexMapping,
    IndexMappingsUserPreferenceKey,
} from 'symbols/indexMappings/store/IndexMappingsState';
import { mapSymbol } from 'symbols/indexMappings/indexMappingsUtils';

export type FillsGraphStreamedDataStreamState =
    StreamState<FillsGraphDataState>;
export type FillsGraphStreamedDataStreamsState =
    StreamsByStreamKeyState<FillsGraphDataState>;

export const initialFillsGraphStreamedDataStreamState: FillsGraphStreamedDataStreamState =
    {
        messages: [],
        data: initialFillsGraphDataState,
    };

export interface FillsGraphStreamedDataConfiguration {
    symbol?: string;
    indexFallback: string;
}

export interface FillsGraphStreamedDataExtraArgs {
    index: string;
}

export const initialFillsGraphStreamedDataConfiguration: FillsGraphStreamedDataConfiguration =
    { indexFallback: '' };

export function toFillsGraphStreamedDataUid(symbol: string, index: string) {
    return `fillsStreamedData-${symbol}|${index}`;
}
function toRequestUid(
    symbol: string,
    _configState: FillsGraphStreamedDataConfiguration,
    _qty?: number,
    extraArgs?: FillsGraphStreamedDataExtraArgs
): string {
    const index = extraArgs?.index ?? '';
    return toFillsGraphStreamedDataUid(symbol, index);
}

export function extractSymbolFromPayload(
    request: FillsGraphByOrderIdOrSymQtyStreamRequestWithSymQty
) {
    const { symQty } = request;
    if (symQty) {
        return symQty.split(',')[0];
    }
    return '';
}

function requestToStreamKey(
    request: FillsGraphByOrderIdOrSymQtyStreamRequestWithSymQty
): string {
    const symbol = extractSymbolFromPayload(request);

    const { indexSym: index } = request;
    return toRequestUid(symbol, { indexFallback: '' }, undefined, { index });
}

function buildRequest(
    symbol: string,
    symbology: SymbologyValues,
    _configState: FillsGraphStreamedDataConfiguration,
    _qty?: number,
    extraArgs?: FillsGraphStreamedDataExtraArgs
): FillsGraphByOrderIdOrSymQtyStreamRequestWithSymQty {
    const fillsGraphConfig = { ...defaultFillsGraphConfiguration };
    fillsGraphConfig.general.fallbackIndex = extraArgs?.index ?? '';
    fillsGraphConfig.general.useSecondarySymbol = false;

    const fakePortfolioSelection = new PortfolioSelectionWrapper(
        buildPortfolioSelection({
            symbology,
            selectedPositions: [{ symbol, qty: 1, qtyRemaining: 0, side: 'B' }],
        })
    );
    return buildFillsGraphRequest(
        [],
        fakePortfolioSelection,
        SymbologyValues.Instinet,

        fillsGraphConfig
    ) as FillsGraphByOrderIdOrSymQtyStreamRequestWithSymQty;
}

export async function buildExtraArgs(
    state: State,
    services: DataServices,
    config: FillsGraphStreamedDataConfiguration,
    symbol?: string
): Promise<FillsGraphStreamedDataExtraArgs> {
    if (!symbol) {
        return { index: '' };
    }
    const { symbology } = selectAppSettings(state);
    const { symbolsService, userPreferencesService } = services;
    const symbolInfo = await symbolsService.getSymbolInfo(symbol, symbology);

    if (!symbolInfo) {
        return { index: '' };
    }
    const userPrefsMappings = await userPreferencesService.fetchUserPreference<
        IndexMapping[]
    >(IndexMappingsUserPreferenceKey);
    const { indexFallback } = config;

    if (indexFallback === 'DEFAULT') {
        return Promise.resolve({ index: 'DEFAULT' });
    }

    let indexFallbackInstinet = indexFallback;
    if (indexFallback && symbology !== SymbologyValues.Instinet) {
        const converted = await symbolsService.convertSymbol(
            indexFallback,
            symbology,
            SymbologyValues.Instinet
        );
        if (converted) {
            indexFallbackInstinet = converted;
        }
    }

    const { index } = mapSymbol(
        indexFallbackInstinet,
        userPrefsMappings ?? [],
        symbolInfo,
        1
    );

    return Promise.resolve({ index });
}

const {
    createWidgetRequestUid,
    getWidgetStreamRequestInfo,
    getStreamRequestInfosForSymbols,
} = buildSymbolDependentRequestHelpers<
    FillsGraphByOrderIdOrSymQtyStreamRequestWithSymQty,
    FillsGraphStreamedDataConfiguration,
    FillsGraphStreamedDataExtraArgs
>(
    toRequestUid,
    requestToStreamKey,
    buildRequest,
    initialFillsGraphStreamedDataConfiguration,
    extractSymbolFromPayload,
    buildExtraArgs
);

export {
    createWidgetRequestUid as createFillsGraphStreamRequestBySymbolRequestUid,
    getWidgetStreamRequestInfo as getFillsGraphStreamRequestBySymbolWithStreamKey,
    getStreamRequestInfosForSymbols as getFillsGraphStreamRequestBySymbolInfosForSymbols,
    toRequestUid as toFillsGraphStreamRequestBySymbolRequestUid,
};
